import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Star, ExternalLink } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };
    fetchReviews();
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? "fill-neon-lime text-neon-lime" : "text-zinc-700"
        }`}
      />
    ));
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)
    : "5.0";

  return (
    <footer data-testid="footer" className="bg-carbon-paper border-t border-zinc-800 relative">
      {/* Carbon texture overlay */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" 
           style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} />
      
      {/* Reviews Section */}
      <section className="py-16 md:py-24 border-b border-zinc-800 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-unbounded text-2xl md:text-4xl font-bold text-white mb-4">
              Avis <span className="text-neon-lime">Clients</span>
            </h2>
            <div className="flex items-center justify-center gap-3">
              <div className="flex items-center gap-1">
                {renderStars(5)}
              </div>
              <span className="font-unbounded text-2xl font-bold text-white">
                {averageRating}
              </span>
              <span className="text-zinc-500 font-mono text-sm">
                / 5 sur Google
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <div
                key={review.id}
                data-testid={`review-card-${index}`}
                className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-zinc-700 transition-colors backdrop-blur-sm"
              >
                <div className="flex items-center gap-1 mb-3">
                  {renderStars(review.rating)}
                </div>
                <p className="text-zinc-300 font-manrope text-sm leading-relaxed mb-4">
                  "{review.text}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{review.author}</p>
                    <p className="text-zinc-500 text-xs font-mono">{review.date}</p>
                  </div>
                  <img
                    src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_light_color_92x30dp.png"
                    alt="Google"
                    className="h-4 opacity-50"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <a
              href="https://g.co/kgs/FastCar"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-neon-lime transition-colors font-mono text-sm"
              data-testid="google-reviews-link"
            >
              <span>Voir tous les avis sur Google</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Main Footer */}
      <div className="py-12 md:py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <Link to="/" className="inline-block mb-6">
                <img
                  src="/assets/Fast & Car/Fichier Logo/Logo Fast & Car transparent.png"
                  alt="Fast&Car"
                  className="h-16"
                />
              </Link>
              <p className="text-zinc-400 font-manrope leading-relaxed max-w-md">
                Mandataire automobile spécialisé dans la recherche personnalisée 
                et l'importation de véhicules depuis l'Europe et la Corée du Sud. 
                Expertise mécanique certifiée.
              </p>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="font-unbounded text-white font-bold uppercase tracking-wider mb-6">
                Navigation
              </h4>
              <nav className="flex flex-col gap-3">
                <Link to="/" className="text-zinc-400 hover:text-neon-lime transition-colors">Accueil</Link>
                <Link to="/process" className="text-zinc-400 hover:text-neon-lime transition-colors">Notre Process</Link>
                <Link to="/services" className="text-zinc-400 hover:text-neon-lime transition-colors">Prestations</Link>
                <Link to="/galerie" className="text-zinc-400 hover:text-neon-lime transition-colors">Galerie</Link>
                <Link to="/simulateur" className="text-zinc-400 hover:text-neon-lime transition-colors">Simulateur</Link>
                <Link to="/contact" className="text-zinc-400 hover:text-neon-lime transition-colors">Contact</Link>
              </nav>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-unbounded text-white font-bold uppercase tracking-wider mb-6">
                Contact
              </h4>
              <div className="flex flex-col gap-4">
                <a
                  href="tel:0768013271"
                  className="flex items-center gap-3 text-zinc-400 hover:text-neon-lime transition-colors group"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 text-neon-lime" />
                  <span className="font-mono">07 68 01 32 71</span>
                </a>
                <a
                  href="mailto:contact@fastandcar.com"
                  className="flex items-center gap-3 text-zinc-400 hover:text-neon-lime transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 text-neon-lime" />
                  <span>contact@fastandcar.com</span>
                </a>
                <div className="flex items-start gap-3 text-zinc-400">
                  <MapPin className="w-5 h-5 text-neon-lime flex-shrink-0 mt-1" />
                  <span>Intervention nationale</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-zinc-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-zinc-500 text-sm font-mono">
              © {new Date().getFullYear()} Fast&Car. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
